import { Fragment, useRef } from 'react';
import type { JSX, ReactNode } from 'react';
import { cn } from '../lib/cn.js';

/**
 * Kit skeleton MyTaki : placeholders de chargement de CONTENU.
 *
 * Doctrine (cf. tokens.css) : skeleton = chargement de contenu d'une zone
 * (zone 5, listes) ; spinner = action ponctuelle ; barre de progression =
 * navigation ; boot screen = demarrage seulement. Une section charge sans
 * bloquer le reste.
 *
 * Tous les elements sont decoratifs : `aria-hidden`, non focusables. Le
 * visuel vient de la classe globale `.taki-skeleton` (tokens --skeleton-base
 * / --skeleton-highlight, reflet anime, statique en prefers-reduced-motion)
 * et des tokens de rayon.
 *
 * Les COMPOSES (SkeletonListRows, SkeletonStats) copient les dimensions des
 * patterns reels du gabarit au rem pres : zero saut de mise en page quand le
 * contenu reel remplace le skeleton.
 */

/* ------------------------------------------------------------------ */
/* Primitifs                                                           */
/* ------------------------------------------------------------------ */

export interface SkeletonBlockProps {
  /** Largeur CSS (rem ou %). Defaut '100%'. */
  w?: string;
  /** Hauteur CSS (rem). Defaut '1rem'. */
  h?: string;
  /** Rayon CSS. Defaut var(--radius-sm). */
  rounded?: string;
  /** Classes complementaires (utilitaires Tailwind du preset). */
  className?: string;
}

/** Bloc rectangulaire generique : la brique de base pour composer un skeleton. */
export function SkeletonBlock({
  w = '100%',
  h = '1rem',
  rounded = 'var(--radius-sm)',
  className,
}: SkeletonBlockProps): JSX.Element {
  return (
    <span
      aria-hidden
      className={cn('taki-skeleton block', className)}
      style={{ width: w, height: h, borderRadius: rounded }}
    />
  );
}

export interface SkeletonTextProps {
  /** Nombre de lignes. Defaut 3. */
  lines?: number;
  /** Classes complementaires. */
  className?: string;
}

/* Largeurs deterministes (pas d'aleatoire : rendu stable d'un render a
   l'autre), cyclees par index de ligne. */
const TEXT_LINE_WIDTHS = ['92%', '78%', '96%', '84%', '88%', '73%'];

/**
 * Paragraphe de texte factice : lignes de largeur variable deterministe,
 * derniere ligne plus courte. Chaque ligne reproduit la boite d'une ligne
 * text-sm (1.25rem) avec une barre de la hauteur de la police (0.875rem).
 */
export function SkeletonText({ lines = 3, className }: SkeletonTextProps): JSX.Element {
  const count = Math.max(1, lines);
  return (
    <span aria-hidden className={cn('block w-full', className)}>
      {Array.from({ length: count }, (_, index) => {
        const isLast = index === count - 1 && count > 1;
        const width = isLast ? '55%' : TEXT_LINE_WIDTHS[index % TEXT_LINE_WIDTHS.length];
        return (
          <span key={index} className="flex h-5 items-center">
            <span className="taki-skeleton block h-3.5 rounded-xs" style={{ width }} />
          </span>
        );
      })}
    </span>
  );
}

export interface SkeletonAvatarProps {
  /** Diametre en px de maquette (rendu en rem, comme Avatar). Defaut 26. */
  size?: number;
  /** Classes complementaires. */
  className?: string;
}

/** Rond a la place d'un Avatar. Meme convention de taille que Avatar (px de maquette / 16 en rem). */
export function SkeletonAvatar({ size = 26, className }: SkeletonAvatarProps): JSX.Element {
  return (
    <span
      aria-hidden
      className={cn('taki-skeleton inline-block shrink-0 rounded-full', className)}
      style={{ width: `${size / 16}rem`, height: `${size / 16}rem` }}
    />
  );
}

export interface SkeletonButtonProps {
  /** Largeur CSS. Defaut '6.5rem'. */
  w?: string;
  /** Classes complementaires. */
  className?: string;
}

/** Bloc a l'empreinte d'un Button md (h-10, rounded-md). */
export function SkeletonButton({ w = '6.5rem', className }: SkeletonButtonProps): JSX.Element {
  return (
    <span
      aria-hidden
      className={cn('taki-skeleton inline-block h-10 shrink-0 rounded-md', className)}
      style={{ width: w }}
    />
  );
}

export interface SkeletonImageProps {
  /** Largeur CSS. Defaut '100%'. */
  w?: string;
  /** Hauteur CSS. Defaut '10rem'. */
  h?: string;
  /** Classes complementaires. */
  className?: string;
}

/** Bloc a la place d'une image ou d'une vignette. */
export function SkeletonImage({ w = '100%', h = '10rem', className }: SkeletonImageProps): JSX.Element {
  return (
    <span
      aria-hidden
      className={cn('taki-skeleton block rounded-lg', className)}
      style={{ width: w, height: h }}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Composes (fideles aux patterns reels du gabarit, zero layout shift)  */
/* ------------------------------------------------------------------ */

export interface SkeletonListRowsProps {
  /** Nombre de lignes factices. Defaut 8. */
  rows?: number;
  /** Rond d'avatar en tete de ligne (pattern de reference). Defaut true. */
  withAvatar?: boolean;
}

/* Largeurs deterministes des barres nom / sous-ligne, cyclees par ligne. */
const ROW_NAME_WIDTHS = ['42%', '34%', '48%', '38%', '45%', '31%'];
const ROW_SUB_WIDTHS = ['58%', '66%', '52%', '61%', '55%', '68%'];

/**
 * Liste factice reproduisant EXACTEMENT la ligne de liste de la zone 5
 * (docs/GABARIT-ECRAN.md section 7, pattern "Les Chats") :
 * `flex items-center gap-3.5 border-b border-border-soft px-5 py-2.5`,
 * Avatar 30, nom text-sm (ligne 1.25rem), sous-ligne text-xs (ligne 1rem).
 * Hauteur de ligne identique au reel (3.5rem + filet) : zero saut au swap.
 */
export function SkeletonListRows({ rows = 8, withAvatar = true }: SkeletonListRowsProps): JSX.Element {
  return (
    <div aria-hidden>
      {Array.from({ length: Math.max(1, rows) }, (_, index) => (
        <div
          key={index}
          className="flex items-center gap-3.5 border-b border-border-soft px-5 py-2.5"
        >
          {withAvatar && <SkeletonAvatar size={30} />}
          <div className="min-w-0 flex-1">
            {/* nom : text-sm, boite de ligne 1.25rem (h-5), barre 0.875rem */}
            <span className="flex h-5 items-center">
              <span
                className="taki-skeleton block h-3.5 rounded-xs"
                style={{ width: ROW_NAME_WIDTHS[index % ROW_NAME_WIDTHS.length] }}
              />
            </span>
            {/* sous-ligne : text-xs, boite de ligne 1rem (h-4), barre 0.625rem */}
            <span className="flex h-4 items-center">
              <span
                className="taki-skeleton block h-2.5 rounded-xs"
                style={{ width: ROW_SUB_WIDTHS[index % ROW_SUB_WIDTHS.length] }}
              />
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

export interface SkeletonStatsProps {
  /** Nombre de statistiques factices. Defaut 2. */
  count?: number;
}

/* Largeurs deterministes des barres valeur / libelle, cyclees par stat. */
const STAT_VALUE_WIDTHS = ['1.75rem', '2.25rem', '1.5rem'];
const STAT_LABEL_WIDTHS = ['3rem', '2.5rem', '3.5rem'];

/**
 * Stats factices reproduisant l'annexe d'en-tete du gabarit (zone 3) :
 * conteneur `flex items-stretch gap-4`, separateur `w-px bg-border-soft`,
 * valeur text-base leading-tight (boite 1.25rem) + libelle text-[0.6875rem]
 * (boite 1.03125rem, line-height herite 1.5), alignes a droite.
 */
export function SkeletonStats({ count = 2 }: SkeletonStatsProps): JSX.Element {
  return (
    <div aria-hidden className="flex shrink-0 items-stretch gap-4">
      {Array.from({ length: Math.max(1, count) }, (_, index) => (
        <Fragment key={index}>
          {index > 0 && <span className="w-px bg-border-soft" />}
          <div className="flex flex-col items-end">
            {/* valeur : text-base leading-tight, boite 1.25rem */}
            <span className="flex h-5 items-center">
              <span
                className="taki-skeleton block h-3.5 rounded-xs"
                style={{ width: STAT_VALUE_WIDTHS[index % STAT_VALUE_WIDTHS.length] }}
              />
            </span>
            {/* libelle : text-[0.6875rem], boite 0.6875rem x 1.5 = 1.03125rem */}
            <span className="flex h-[1.03125rem] items-center">
              <span
                className="taki-skeleton block h-2 rounded-xs"
                style={{ width: STAT_LABEL_WIDTHS[index % STAT_LABEL_WIDTHS.length] }}
              />
            </span>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Wrapper                                                             */
/* ------------------------------------------------------------------ */

export interface SkeletonProps {
  /** Vrai tant que la donnee charge : rend `skeleton` a la place des children. */
  loading: boolean;
  /** Le skeleton a afficher pendant le chargement (primitifs ou composes ci-dessus). */
  skeleton: ReactNode;
  /** Le contenu reel, monte avec .taki-anim-fade au swap skeleton -> contenu. */
  children?: ReactNode;
}

/**
 * Wrapper declaratif : `<Skeleton loading={l} skeleton={<SkeletonText />}>...`.
 * Pendant le chargement, le skeleton est rendu dans un conteneur `aria-hidden` ;
 * au swap, le contenu reel monte avec un fondu (.taki-anim-fade). Un contenu
 * rendu sans jamais etre passe par le skeleton monte sans wrapper ni fondu.
 */
export function Skeleton({ loading, skeleton, children }: SkeletonProps): JSX.Element {
  // Memorise "un skeleton a ete montre" pour ne fondre le contenu QU'AU swap
  // (pas au premier rendu d'un ecran deja charge).
  const hadSkeletonRef = useRef(false);
  if (loading) {
    hadSkeletonRef.current = true;
    return (
      <div key="skeleton" aria-hidden>
        {skeleton}
      </div>
    );
  }
  if (hadSkeletonRef.current) {
    return (
      <div key="content" className="taki-anim-fade">
        {children}
      </div>
    );
  }
  return <Fragment>{children}</Fragment>;
}
